import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';

const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';

function ReportsDashboard() {
    const [status, setStatus] = useState(null);

    // Service Report
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    // Employee Report
    const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);


const generateMembershipCards = async () => {
    showStatus('Preparing membership cards...', 'loading');
    try {
        const response = await axios({
            url: `${API_BASE_URL}/membership-cards`,
            method: 'GET',
            responseType: 'arraybuffer',  // ← CRITICAL CHANGE: 'arraybuffer' instead of 'blob'
        });

        // Safety check: real PDF with 871 users should be >1MB


        // Wrap ArrayBuffer in Blob with correct type
        const blob = new Blob([response.data], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Membership_Cards.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showStatus('Membership cards downloaded successfully!', 'success');
    } catch (err) {
        console.error("Card generation failed:", err);
        showStatus(`Error: ${err.message || 'Failed to download PDF'}`, 'error');
    }
};

    // Filters
    const [employee, setEmployee] = useState('All');
    const [paymentMethod, setPaymentMethod] = useState('All');
    const [block, setBlock] = useState('All');
    const [billingMonth, setBillingMonth] = useState('');

    // Dropdown data
    const [employees, setEmployees] = useState([{ employee_id: 'All', full_name: 'All' }]);
    const [paymentMethods, setPaymentMethods] = useState(['All']);
    const [blocks, setBlocks] = useState(['All']);

    // Bulk upload & null invoices
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [nullInvoices, setNullInvoices] = useState([]);

    const showStatus = (msg, type = 'info') => {
        const colors = {
            success: '#d4edda',
            error: '#f8d7da',
            info: '#d1ecf1',
            loading: '#fff3cd'
        };
        setStatus({ msg, type, color: colors[type] || '#e2e3e5' });
        setTimeout(() => setStatus(null), 5000);
    };

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [empRes, pmRes, blkRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/reports/employees`),
                    axios.get(`${API_BASE_URL}/reports/payment-methods`),
                    axios.get(`${API_BASE_URL}/reports/blocks`)
                ]);
                const employeeObjects = empRes.data.map(emp => ({
                    employee_id: emp.employee_id,
                    full_name: `${emp.first_name} ${emp.last_name}`
                }));

                setEmployees([{ employee_id: 'All', full_name: 'All' }, ...employeeObjects]);
                setPaymentMethods(['All', ...pmRes.data]);
                setBlocks(['All', ...blkRes.data]);
            } catch (err) {
                showStatus('Failed to load filters', 'error');
            }
        };
        loadFilters();

        const loadNullInvoices = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/reports/null-description-invoices`);
                setNullInvoices(res.data);
            } catch (err) {
                showStatus('Failed to load null description invoices', 'error');
            }
        };
        loadNullInvoices();
    }, []);

    const generateServiceReport = async () => {
        if (!month || !year) return showStatus('Select month and year', 'error');
        showStatus('Generating service report...', 'loading');
        try {
            const res = await axios.get(`${API_BASE_URL}/reports/service-collection`, {
                params: { month, year },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `Service_Collection_${year}-${String(month).padStart(2, '0')}.csv`;
            link.click();
            showStatus('Service report downloaded!', 'success');
        } catch (err) {
            showStatus('Failed to generate service report', 'error');
        }
    };

    const generateEmployeeReport = async () => {
        if (!reportDate) return showStatus('Select a date', 'error');
        showStatus('Generating employee report...', 'loading');

        const params = { date: reportDate };
        if (employee && employee !== 'All') params.employee = employee;
        if (paymentMethod && paymentMethod !== 'All') params.payment_method = paymentMethod;
        if (block && block !== 'All') params.block = block;
        if (billingMonth) params.billing_month = billingMonth;

        try {
            const response = await axios({
                method: 'get',
                url: `${API_BASE_URL}/reports/employee-collection`,
                params,
                responseType: 'blob',
                headers: { 'Accept': 'text/csv' }
            });

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Employee_Collection_${reportDate}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showStatus('Employee report downloaded!', 'success');
        } catch (err) {
            const text = err.response?.data ? await err.response.data.text() : err.message;
            showStatus(`Error: ${text}`, 'error');
        }
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) return showStatus('Select a file to upload.', 'error');
        const formData = new FormData();
        formData.append('file', bulkFile);
        showStatus('Uploading descriptions...', 'loading');
        try {
            await axios.post(`${API_BASE_URL}/reports/bulk-update-descriptions`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showStatus('Descriptions updated successfully!', 'success');
            setBulkModalOpen(false);
            setBulkFile(null);
            const res = await axios.get(`${API_BASE_URL}/reports/null-description-invoices`);
            setNullInvoices(res.data);
        } catch (err) {
            showStatus(err.response?.data?.error || 'Upload failed', 'error');
        }
    };

    const TEMPLATE_HEADERS = [
        { label: "Transaction Date (YYYY-MM-DD)", key: "transaction_date" },
        { label: "Reference Number", key: "reference_number" },
        { label: "Account Number", key: "account_number" },
        { label: "Description", key: "description" }
    ];

    return (
        <div style={styles.page}>
            <h1 style={styles.title}>Reports Dashboard</h1>
            {status && (
                <div style={{ ...styles.status, backgroundColor: status.color }}>
                    {status.msg}
                </div>
            )}

            {/* 1. Monthly Service Collection Report */}
            <div style={styles.card}>
                <h2 style={styles.cardTitle}>1. Monthly Service Collection Report</h2>
                <p style={styles.desc}>
                    Shows <strong>daily collections per service</strong> for the selected month:<br />
                    • Connection Fees • Labour Charge • Water Bill (Monthly) • Reconnection Fees<br />
                    <strong>Based on payment date — not invoice date.</strong>
                </p>
                <div style={styles.row}>
                    <select value={month} onChange={e => setMonth(+e.target.value)} style={styles.select}>
                        {[...Array(12)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {new Date(0, i).toLocaleString('en', { month: 'long' })}
                            </option>
                        ))}
                    </select>
                    <input
                        type="number"
                        value={year}
                        onChange={e => setYear(+e.target.value)}
                        min="2000"
                        max="2100"
                        style={styles.input}
                        placeholder="Year"
                    />
                    <button onClick={generateServiceReport} style={styles.btn}>
                        Generate CSV
                    </button>
                </div>
            </div>

            {/* 2. Daily Employee Collection Report */}
            <div style={styles.card}>
                <h2 style={styles.cardTitle}>2. Daily Employee Collection Report</h2>
                <p style={styles.desc}>Shows payments recorded by each employee on a selected date</p>

                <div style={styles.filters}>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Date *</label>
                        <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} style={styles.input} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Employee</label>
                        <select value={employee} onChange={e => setEmployee(e.target.value)} style={styles.select}>
                            {employees.map(emp => (
                                <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Payment Method</label>
                        <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={styles.select}>
                            {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Block</label>
                        <select value={block} onChange={e => setBlock(e.target.value)} style={styles.select}>
                            {blocks.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>Billing Month</label>
                        <input type="month" value={billingMonth} onChange={e => setBillingMonth(e.target.value)} style={styles.input} />
                    </div>
                </div>

                <button onClick={generateEmployeeReport} style={styles.btn}>Generate Filtered CSV</button>
                <button onClick={() => setBulkModalOpen(true)} style={{ ...styles.btn, marginTop: '15px', backgroundColor: '#28a745' }}>
                    Upload Bulk Descriptions
                </button>
            </div>

<div style={styles.card}>
        <h2 style={styles.cardTitle}>3. Membership Management</h2>
        <p style={{ fontSize: '0.9em', color: '#666' }}>Generate printable A4 sheets with 12 membership cards each.</p>
        <button 
            onClick={generateMembershipCards}
            style={{ 
                ...styles.btn, 
                backgroundColor: '#28a745', 
                marginTop: '10px',
                width: '100%' 
            }}
        >
            🖨️ Download Membership Cards (PDF)
        </button>
    </div>

            {/* Invoices with No Description */}
            <div style={styles.card}>
                <h2 style={styles.cardTitle}>4. Invoices with No Description</h2>
                <p style={styles.desc}>
                    List of invoices missing descriptions. Use bulk upload to fix them.
                </p>
                {nullInvoices.length > 0 ? (
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeaderRow}>
                                <th style={styles.tableHeader}>Account</th>
                                <th style={styles.tableHeader}>Invoice Date</th>
                                <th style={styles.tableHeader}>Reference</th>
                                <th style={styles.tableHeader}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {nullInvoices.map((inv, i) => (
                                <tr key={i}>
                                    <td style={styles.tableCell}>{inv.account_number}</td>
                                    <td style={styles.tableCell}>{inv.transaction_date}</td>
                                    <td style={styles.tableCell}>{inv.reference_number}</td>
                                    <td style={styles.tableCell}>Ksh {inv.amount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p style={styles.noHistory}>No invoices with missing descriptions.</p>
                )}
            </div>

            {/* Bulk Upload Modal */}
            {bulkModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.bulkModalContent}>
                        <h3 style={styles.modalHeader}>Bulk Update Invoice Descriptions</h3>
                        <p style={styles.instructionsText}>
                            <strong>Instructions:</strong> Download template → fill → upload to update descriptions.
                        </p>
                        <div style={styles.downloadSection}>
                            <span style={styles.downloadText}>1. Download Template:</span>
                            <CSVLink data={[]} headers={TEMPLATE_HEADERS} filename="bulk_invoice_template.csv" style={styles.downloadButton}>
                                Download Template (.csv)
                            </CSVLink>
                        </div>
                        <div style={styles.uploadSection}>
                            <span style={styles.downloadText}>2. Upload File:</span>
                            <input type="file" accept=".csv" onChange={e => setBulkFile(e.target.files[0])} style={styles.fileInput} />
                        </div>
                        <div style={styles.modalFooter}>
                            <button style={{ ...styles.submitButton, backgroundColor: '#6c757d', marginRight: '10px' }} onClick={() => setBulkModalOpen(false)}>
                                Cancel
                            </button>
                            <button
                                style={{ ...styles.submitButton, backgroundColor: bulkFile ? '#007bff' : '#adb5bd' }}
                                onClick={handleBulkUpload}
                                disabled={!bulkFile}
                            >
                                Upload
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: { maxWidth: 1100, margin: '30px auto', padding: 20, fontFamily: 'Arial, sans-serif' },
    title: { textAlign: 'center', color: '#1a73e8', marginBottom: 30 },
    status: { padding: '12px 16px', borderRadius: 6, marginBottom: 20, fontWeight: 600 },
    card: { background: '#fff', border: '1px solid #ddd', borderRadius: 10, padding: 20, marginBottom: 25, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
    cardTitle: { margin: '0 0 10px', color: '#333', fontSize: '1.3em' },
    desc: { margin: '0 0 15px', color: '#555', fontSize: '0.95em', lineHeight: 1.5 },
    row: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    filters: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 15, marginBottom: 20 },
    filterGroup: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: '0.9em', fontWeight: '600', marginBottom: 5, color: '#444' },
    input: { padding: '8px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 },
    select: { padding: '8px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 },
    btn: { background: '#1a73e8', color: 'white', border: 'none', padding: '12px 20px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 15 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 10 },
    tableHeaderRow: { backgroundColor: '#f1f1f1' },
    tableHeader: { padding: '12px 15px', textAlign: 'left', fontWeight: '700', color: '#555', borderBottom: '2px solid #ddd' },
    tableCell: { padding: '12px 15px', borderBottom: '1px solid #eee' },
    noHistory: { textAlign: 'center', padding: '20px', color: '#777', backgroundColor: '#f5f5f5', borderRadius: '6px' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    bulkModalContent: { backgroundColor: '#fff', padding: '30px', borderRadius: '8px', width: '500px', maxHeight: '90%', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' },
    modalHeader: { marginBottom: '20px', fontSize: '1.5em', fontWeight: '700', color: '#007bff', borderBottom: '1px solid #eee', paddingBottom: '10px' },
    instructionsText: { marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderLeft: '4px solid #007bff', borderRadius: '4px', fontSize: '0.9em' },
    downloadSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '6px', marginBottom: '15px' },
    downloadText: { fontWeight: '600', color: '#333' },
    downloadButton: { padding: '8px 15px', backgroundColor: '#28a745', color: 'white', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold' },
    uploadSection: { padding: '15px', backgroundColor: '#f1f1f1', borderRadius: '6px', marginBottom: '20px' },
    fileInput: { width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '6px', marginTop: '10px', backgroundColor: '#fff' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid #eee' },
    submitButton: { width: 'auto', padding: '12px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }
};

export default ReportsDashboard;